/**
 * Shopify Support Popup Controller
 * 
 * 3 Precise Triggers:
 * 1. Customer stays in the cart drawer for > 5 seconds -> Shows popup automatically (drawer stays open behind it)
 * 2. Customer clicks close button (x) on cart drawer -> Shows popup immediately
 * 3. Customer returns from checkout -> Shows popup and closes cart drawer
 * 
 * Bulletproof Protection:
 * - Opening the cart drawer will NEVER accidentally trigger the popup!
 * - Stale session flags are cleared automatically on load.
 */

(function () {
  'use strict';

  if (window.__supportPopupInitialized) return;
  window.__supportPopupInitialized = true;

  const STORAGE_KEYS = {
    WENT_TO_CHECKOUT: 'support_popup_went_to_checkout',
    CHECKOUT_TIMESTAMP: 'support_popup_checkout_timestamp',
    COMPLETED: 'support_popup_completed',
    ORDER_COMPLETED: 'support_popup_order_completed'
  };

  const Storage = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, val) {
      try {
        sessionStorage.setItem(key, val);
      } catch (e) {}
    },
    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {}
    }
  };

  class SupportPopupController {
    constructor() {
      this.popupEl = null;
      this.config = {};
      this.isSubmitting = false;
      this.lastFocusedElement = null;

      // Cart drawer state tracking
      this._cartDrawerIsOpen = false;
      this._cartOpenedAt = 0;
      this._dwellInterval = null;
      this._lastCloseHandled = 0;

      this.init();
    }

    init() {
      // Clear any legacy / stale checkout flags to ensure fresh state on page load
      Storage.remove('support_popup_returned_from_checkout');
      Storage.remove('support_popup_cart_closed_at');

      this.loadConfig();
      this.bindDOM();
      this.bindCheckoutTracking();
      this.checkCheckoutReturn();
      this.bindCartDrawerEvents();
      this.ensureBodyPlacement();

      // Direct open parameter: ?test_popup=now or ?test_popup=open
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('test_popup') === 'now' || params.get('test_popup') === 'open') {
          setTimeout(() => this.open('direct_url'), 400);
        }
      } catch (e) {}

      // Mobile BFCache support
      window.addEventListener('pageshow', () => {
        this.checkCheckoutReturn();
      });

      console.log(`[Support Popup] Ready. Compact 380px modal. Dwell timer: ${this.config.openTimerSeconds}s.`);
    }

    /**
     * Load settings from JSON script element or window global
     */
    loadConfig() {
      let configData = window.supportPopupConfig || {};

      const configScript = document.querySelector('script[id^="SupportPopupConfig-"]');
      if (configScript) {
        try {
          const parsed = JSON.parse(configScript.textContent);
          configData = Object.assign({}, parsed, configData);
        } catch (err) {
          console.error('Support Popup: Error parsing config JSON', err);
        }
      }

      this.config = {
        enabled: configData.enabled !== false,
        heading: configData.heading || 'THE URBAN EDIT',
        openTimerSeconds: Number(configData.openTimerSeconds) || 5,
        triggerOnClose: configData.triggerOnClose !== false,
        triggerOnTimer: configData.triggerOnTimer !== false,
        triggerOnCheckoutReturn: configData.triggerOnCheckoutReturn !== false,
        webhookUrl: (configData.webhookUrl || '').trim(),
        shopUrl: configData.shopUrl || window.location.origin,
        cartUrl: configData.cartUrl || `${window.location.origin}/cart`
      };
    }

    /**
     * Ensure .support-popup is attached directly to document.body
     */
    ensureBodyPlacement() {
      if (!this.popupEl) {
        this.popupEl = document.querySelector('.support-popup');
      }
      if (this.popupEl && this.popupEl.parentElement !== document.body) {
        document.body.appendChild(this.popupEl);
      }
    }

    /**
     * Bind popup UI elements and buttons
     */
    bindDOM() {
      this.popupEl = document.querySelector('.support-popup');
      if (!this.popupEl) {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.bindDOM());
        }
        return;
      }

      this.ensureBodyPlacement();

      this.dialogEl = this.popupEl.querySelector('.support-popup__dialog');
      this.viewForm = this.popupEl.querySelector('[data-support-popup-view="form"]');
      this.viewSuccess = this.popupEl.querySelector('[data-support-popup-view="success"]');

      this.formEl = this.popupEl.querySelector('[data-support-popup-form]');
      this.submitBtn = this.popupEl.querySelector('[data-support-popup-submit]');
      this.errorBanner = this.popupEl.querySelector('[data-support-popup-error]');

      // Close handlers
      this.popupEl.querySelectorAll('[data-support-popup-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Form submission
      if (this.formEl) {
        this.formEl.addEventListener('submit', (e) => {
          e.preventDefault();
          this.handleFormSubmit();
        });

        this.formEl.querySelectorAll('input, textarea').forEach((input) => {
          input.addEventListener('input', () => {
            this.clearFieldError(input.name);
          });
        });

        // Numeric-only restriction & auto-strip India code (+91 / 0) for 10-digit phone number
        const phoneInput = this.formEl.querySelector('input[name="phone"]');
        if (phoneInput) {
          phoneInput.addEventListener('keypress', (e) => {
            // Block any non-digit keys
            if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
              e.preventDefault();
            }
          });

          phoneInput.addEventListener('input', (e) => {
            let digits = e.target.value.replace(/\D/g, '');
            // If user pastes +91... or 91... (12 digits)
            if (digits.length === 12 && digits.startsWith('91')) {
              digits = digits.substring(2);
            } else if (digits.length === 11 && digits.startsWith('0')) {
              digits = digits.substring(1);
            }
            if (digits.length > 10) {
              digits = digits.substring(0, 10);
            }
            if (e.target.value !== digits) {
              e.target.value = digits;
            }
            this.clearFieldError('phone');
          });
        }
      }

      // Escape key to close popup
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    /**
     * Track checkout buttons, forms, and third-party gateways (Razorpay, GoKwik)
     */
    bindCheckoutTracking() {
      const checkoutSelectors = [
        'button[name="checkout"]',
        'input[name="checkout"]',
        '.cart__checkout',
        '[data-cart-checkout-button]',
        'a[href*="/checkout"]',
        'a[href*="/cart/checkout"]',
        'form[action*="/checkout"] button[type="submit"]',
        'form[action*="/checkout"] input[type="submit"]',
        '.additional-checkout-buttons button',
        '.additional-checkout-button',
        '[data-checkout-buttons] button',
        '.gokwik-checkout',
        '[data-gokwik]',
        '#magic-checkout-data-container',
        '.rzp-btn'
      ];

      const recordCheckoutDeparture = () => {
        Storage.set(STORAGE_KEYS.WENT_TO_CHECKOUT, 'true');
        Storage.set(STORAGE_KEYS.CHECKOUT_TIMESTAMP, Date.now().toString());
        console.log('🛍️ [Support Popup] Customer clicked checkout.');
      };

      document.addEventListener('click', (e) => {
        const target = e.target.closest(checkoutSelectors.join(', '));
        if (target) {
          recordCheckoutDeparture();
        } else {
          const btn = e.target.closest('button, a, input[type="submit"]');
          if (btn) {
            const text = (btn.textContent || btn.value || '').trim().toLowerCase();
            if (text === 'checkout' || text === 'proceed to checkout' || text === 'buy it now') {
              recordCheckoutDeparture();
            }
          }
        }
      }, true);

      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form && form.action && (form.action.includes('/checkout') || form.querySelector('button[name="checkout"]'))) {
          recordCheckoutDeparture();
        }
      }, true);

      // Listen for in-page checkout events (Razorpay Magic / GoKwik)
      window.addEventListener('message', (event) => {
        try {
          const data = event.data;

          // 1. Payment / Order Success Event
          const isSuccessMsg = data && (
            data.name === 'magic_checkout_success' ||
            data.event === 'payment.success' ||
            data.type === 'rzp_payment_success' ||
            (typeof data === 'string' && (data.includes('success') || data.includes('completed')))
          );
          if (isSuccessMsg) {
            Storage.set(STORAGE_KEYS.ORDER_COMPLETED, 'true');
            Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
            Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
            return;
          }

          // 2. Checkout Modal Closed WITHOUT completion
          const isCloseMsg = data && (
            data.name === 'magic_checkout_closed' ||
            data.action === 'close' ||
            (typeof data === 'string' && data.includes('close'))
          );
          if (isCloseMsg) {
            try {
              sessionStorage.removeItem('buy_now_in_progress');
              window.buyNowInProgress = false;
            } catch (e) {}

            // Do NOT trigger if order was completed
            if (Storage.get(STORAGE_KEYS.ORDER_COMPLETED) === 'true') {
              Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
              Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
              return;
            }

            if (Storage.get(STORAGE_KEYS.WENT_TO_CHECKOUT) === 'true') {
              if (this.config.triggerOnCheckoutReturn) {
                this.triggerCheckoutReturn('in_page_modal_close');
              }
            }
          }
        } catch (e) {}
      });
    }

    /**
     * Single unified checkout return handler to guarantee ONLY 1 popup triggers,
     * and ONLY when order was NOT completed (cart item count > 0).
     */
    triggerCheckoutReturn(source) {
      if (!this.config.enabled || !this.config.triggerOnCheckoutReturn) return;
      if (this._checkoutReturnHandled) return;

      // Rule 1: Skip popup on Thank You / Order Confirmation routes or if order completed flag is set
      const pathname = window.location.pathname.toLowerCase();
      const isThankYouPage = pathname.includes('/thank_you') ||
                             pathname.includes('/thank-you') ||
                             pathname.includes('/orders/') ||
                             pathname.includes('/order-confirmation');

      if (isThankYouPage || Storage.get(STORAGE_KEYS.ORDER_COMPLETED) === 'true') {
        console.log('🛍️ [Support Popup] Order completed or Thank You page. Skipping popup.');
        Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
        Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
        return;
      }

      // Rule 2: Verify Cart is NOT empty via /cart.js (If order completed, Shopify empties cart)
      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartUrl = rootUrl.replace(/\/$/, '') + '/cart.js';

      fetch(cartUrl)
        .then((res) => {
          if (!res.ok) throw new Error('Cart fetch failed');
          return res.json();
        })
        .then((cart) => {
          if (!cart || cart.item_count === 0) {
            console.log('🛍️ [Support Popup] Cart is empty (Order completed). Skipping popup.');
            Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
            Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
            return;
          }

          // Cart item_count > 0: Customer returned WITHOUT completing order!
          this._checkoutReturnHandled = true;
          this._popupShownInThisCartOpen = true;
          this._openingInProgress = true;

          console.log(`↩️ [Support Popup] Incomplete checkout return via ${source} (cart has ${cart.item_count} items). Triggering popup.`);
          Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
          Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);

          this.closeCartDrawerQuietly();

          setTimeout(() => {
            this._openingInProgress = false;
            this.open('checkout_return');
          }, 1500);
        })
        .catch((err) => {
          console.warn('[Support Popup] Error checking cart status on checkout return:', err);
          Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
          Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
        });
    }

    /**
     * Check if customer returned from checkout page
     */
    checkCheckoutReturn() {
      const pathname = window.location.pathname.toLowerCase();
      const isCheckoutPage = pathname.includes('/checkouts/') || pathname.includes('/checkout');
      const isThankYouPage = pathname.includes('/thank_you') || pathname.includes('/thank-you') || pathname.includes('/orders/');

      if (isCheckoutPage) return;

      if (isThankYouPage) {
        // Clear all checkout return flags on Thank You page
        Storage.set(STORAGE_KEYS.ORDER_COMPLETED, 'true');
        Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
        Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
        return;
      }

      const referrer = document.referrer || '';
      const cameFromCheckoutReferrer = referrer.includes('/checkouts/') ||
                                       referrer.includes('/checkout') ||
                                       referrer.includes('checkout.shopify.com') ||
                                       referrer.includes('shop.app/checkout') ||
                                       referrer.includes('razorpay.com') ||
                                       referrer.includes('gokwik');

      const hadWentToCheckoutFlag = Storage.get(STORAGE_KEYS.WENT_TO_CHECKOUT) === 'true';
      const checkoutTimestamp = parseInt(Storage.get(STORAGE_KEYS.CHECKOUT_TIMESTAMP) || '0', 10);
      const isRecentCheckout = (Date.now() - checkoutTimestamp) < (15 * 60 * 1000); // within 15 mins

      if ((cameFromCheckoutReferrer || hadWentToCheckoutFlag) && isRecentCheckout) {
        if (this.config.triggerOnCheckoutReturn) {
          this.triggerCheckoutReturn('checkout_return');
        }
      } else {
        // Clear stale flags
        Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
        Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
      }
    }

    /**
     * Bind cart drawer open, close, and dwell events
     */
    bindCartDrawerEvents() {
      // 1. Broadcast Theme custom events
      document.addEventListener('theme:cart-drawer:open', () => {
        this.onCartDrawerOpened();
      });

      document.addEventListener('theme:cart-drawer:close', () => {
        if (!this._openingInProgress) {
          this.onCartDrawerCloseTriggered('theme_close_event');
        }
      });

      // 2. Click Delegation on Close Button & Toggle Elements
      document.addEventListener('click', (e) => {
        // ALWAYS IGNORE clicks on cart toggle/open elements! Opening the cart drawer NEVER triggers close!
        const isOpenToggle = e.target.closest(
          '[data-cart-toggle], [data-cart-drawer-show], .navlink--cart, [href="/cart"], .header__cart__status__holder, cart-drawer-toggle'
        );
        if (isOpenToggle) {
          // Mark opening in progress to guarantee no close events run
          this._openingInProgress = true;
          setTimeout(() => {
            this._openingInProgress = false;
            const drawer = document.querySelector('cart-drawer');
            if (drawer && drawer.classList.contains('is-open')) {
              this.onCartDrawerOpened();
            }
          }, 200);
          return;
        }

        // CLOSE BUTTON CLICKED: Only trigger if explicitly clicking close button on an open drawer
        const closeBtn = e.target.closest('[data-cart-drawer-close], .drawer__close');
        if (closeBtn) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.classList.contains('is-open')) {
            this.onCartDrawerCloseTriggered('close_button');
          }
          return;
        }

        // Click outside on dark underlay
        const underlay = e.target.closest('[data-drawer-underlay]');
        if (underlay) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.classList.contains('is-open')) {
            this.onCartDrawerCloseTriggered('underlay_click');
          }
        }
      }, true);

      // Escape key to close cart drawer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._cartDrawerIsOpen && !this.isOpen()) {
          this.onCartDrawerCloseTriggered('escape_key');
        }
      });

      // 3. MutationObserver on cart-drawer element
      const attachObserver = () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || cartDrawer.__spAttached) return;
        cartDrawer.__spAttached = true;

        let wasOpen = cartDrawer.classList.contains('is-open');

        const observer = new MutationObserver(() => {
          const isOpen = cartDrawer.classList.contains('is-open');
          if (isOpen && !wasOpen) {
            wasOpen = true;
            this.onCartDrawerOpened();
          } else if (!isOpen && wasOpen) {
            wasOpen = false;
            // Only trigger close if we were not just opening
            if (!this._openingInProgress) {
              this.onCartDrawerCloseTriggered('mutation_close');
            }
          }
        });

        observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      };

      attachObserver();
      document.addEventListener('DOMContentLoaded', attachObserver);
    }

    /**
     * When cart drawer opens:
     * - Marks drawer as OPEN
     * - Records opening timestamp
     * - Starts Trigger 1 (Dwell Timer for 5 seconds)
     */
    onCartDrawerOpened() {
      if (!this.config.enabled) return;
      if (this.isOpen()) return;

      this._cartDrawerIsOpen = true;
      this._cartOpenedAt = Date.now();
      this._popupShownInThisCartOpen = false; // Reset for new cart interaction

      console.log('🛒 [Support Popup] Cart drawer opened.');

      // Start Trigger 1: Customer stays in cart drawer for > 5 seconds
      if (this.config.triggerOnTimer) {
        this.startCartOpenTimer();
      }
    }

    /**
     * Start tracking dwell time in cart drawer
     */
    startCartOpenTimer() {
      this.stopCartOpenTimer();
      if (!this.config.enabled || !this.config.triggerOnTimer) return;

      const openStart = Date.now();
      const timerSeconds = this.config.openTimerSeconds || 5;

      console.log(`⏱️ [Support Popup] Tracking ${timerSeconds}-second dwell timer in cart drawer...`);

      this._dwellInterval = setInterval(() => {
        const drawer = document.querySelector('cart-drawer');
        const isOpen = drawer && drawer.classList.contains('is-open');

        if (!isOpen) {
          this.stopCartOpenTimer();
          return;
        }

        const elapsed = (Date.now() - openStart) / 1000;
        if (elapsed >= timerSeconds && !this.isOpen()) {
          console.log(`🎉 [Support Popup] ${timerSeconds}-Second Timer Triggered! (Stayed in drawer for ${elapsed.toFixed(1)}s >= ${timerSeconds}s)`);
          this.stopCartOpenTimer();

          // Mark popup as shown during this cart opening so closing the drawer won't re-trigger it
          this._popupShownInThisCartOpen = true;

          // Drawer stays open in background - show Support Popup on top
          this.open('cart_drawer_timer');
        }
      }, 250);
    }

    stopCartOpenTimer() {
      if (this._dwellInterval) {
        clearInterval(this._dwellInterval);
        this._dwellInterval = null;
      }
    }

    /**
     * When cart drawer is closed:
     * - Prevents false triggers: ONLY fires if the cart drawer was actually open for >= 600ms!
     * - Prevents opening on the initial cart drawer click!
     */
    onCartDrawerCloseTriggered(source) {
      this.stopCartOpenTimer();

      if (!this.config.enabled) return;
      if (!this.config.triggerOnClose) return;
      if (this.isOpen()) return;
      if (this._openingInProgress) return;
      if (this._checkoutReturnHandled) return;

      // STRICT PROTECTION: If drawer was not open, or opened less than 600ms ago: IGNORE!
      const timeSinceOpen = Date.now() - this._cartOpenedAt;
      if (!this._cartDrawerIsOpen || timeSinceOpen < 600) {
        return;
      }
      this._cartDrawerIsOpen = false;

      // ONE OFFER PER CART VISIT:
      // If the popup was already displayed while this cart drawer was open (e.g. via the timer),
      // DO NOT show it again when the customer closes the cart!
      if (this._popupShownInThisCartOpen) {
        console.log('ℹ️ [Support Popup] Popup was already displayed during this cart visit. Skipping close trigger.');
        this._popupShownInThisCartOpen = false;
        return;
      }

      // Debounce close events within 350ms
      const now = Date.now();
      if (this._lastCloseHandled && (now - this._lastCloseHandled < 350)) {
        return;
      }
      this._lastCloseHandled = now;

      if (this.config.triggerOnClose) {
        this._popupShownInThisCartOpen = true;
        console.log(`🎉 [Support Popup] Customer closed the cart drawer (via ${source}, was open for ${(timeSinceOpen / 1000).toFixed(1)}s). Showing "THE URBAN EDIT" popup.`);
        setTimeout(() => {
          this.open('cart_drawer_closed');
        }, 120);
      }
    }

    /**
     * Close the cart drawer quietly without focus conflicts
     */
    closeCartDrawerQuietly() {
      this._cartDrawerIsOpen = false;
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        if (typeof cartDrawer.closeCartDrawer === 'function') {
          cartDrawer.closeCartDrawer();
        }
        cartDrawer.classList.remove('is-open');
        cartDrawer.classList.remove('is-closing');
      }
      const underlay = document.querySelector('[data-drawer-underlay]');
      if (underlay) {
        underlay.classList.remove('is-visible');
      }
      if (typeof window.isBuyNowOrCheckoutActive !== 'function' || !window.isBuyNowOrCheckoutActive()) {
        document.documentElement.removeAttribute('data-scroll-locked');
      }
    }

    /**
     * Open Support Popup modal
     */
    open(triggerReason = 'manual') {
      this.ensureBodyPlacement();
      if (!this.popupEl) this.bindDOM();
      if (!this.popupEl) return;

      this.lastFocusedElement = document.activeElement;
      this.showFormView();

      this.popupEl.removeAttribute('hidden');
      this.popupEl.setAttribute('aria-hidden', 'false');
      
      requestAnimationFrame(() => {
        if (!this.popupEl) return;
        this.popupEl.classList.add('is-active');
        document.body.classList.add('support-popup-active');
      });

      const firstInput = this.popupEl.querySelector('input[name="name"]') ||
                          this.popupEl.querySelector('.support-popup__close-btn');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 300);
      }

      this.trapFocus();
      console.log(`✅ [Support Popup] Opened successfully! (Reason: ${triggerReason})`);
    }

    /**
     * Close Support Popup modal
     */
    close() {
      if (!this.popupEl || !this.isOpen()) return;

      this.popupEl.classList.remove('is-active');
      document.body.classList.remove('support-popup-active');

      setTimeout(() => {
        if (!this.isOpen()) {
          this.popupEl.setAttribute('aria-hidden', 'true');
          this.popupEl.setAttribute('hidden', '');
          if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
            this.lastFocusedElement.focus();
          }
        }
      }, 280);
    }

    isOpen() {
      return this.popupEl && this.popupEl.classList.contains('is-active');
    }

    showFormView() {
      if (this.viewSuccess) this.viewSuccess.setAttribute('hidden', '');
      if (this.viewForm) this.viewForm.removeAttribute('hidden');
      this.clearAlert();
    }

    showSuccessView() {
      if (this.viewForm) this.viewForm.setAttribute('hidden', '');
      if (this.viewSuccess) this.viewSuccess.removeAttribute('hidden');
    }

    /**
     * Form Validation (Name, 10-Digit Phone Number, Question/Query)
     */
    validateForm() {
      let isValid = true;
      const nameInput = this.formEl.querySelector('input[name="name"]');
      const phoneInput = this.formEl.querySelector('input[name="phone"]') ||
                         this.formEl.querySelector('input[name="contact"]');
      const questionInput = this.formEl.querySelector('textarea[name="question"]');

      // 1. Name validation
      if (!nameInput || !nameInput.value.trim()) {
        this.showFieldError('name', 'Please enter your name.');
        isValid = false;
      } else {
        this.clearFieldError('name');
      }

      // 2. Phone validation: Number only & exactly 10 digits
      if (phoneInput) {
        const fieldName = phoneInput.name;
        const phoneVal = phoneInput.value.trim();
        const digitsOnly = phoneVal.replace(/\D/g, '');

        if (!phoneVal) {
          this.showFieldError(fieldName, 'Please enter your 10-digit mobile number.');
          isValid = false;
        } else if (!/^\d+$/.test(phoneVal)) {
          this.showFieldError(fieldName, 'Phone number must contain numbers only.');
          isValid = false;
        } else if (digitsOnly.length !== 10) {
          this.showFieldError(fieldName, 'Please enter a valid 10-digit mobile number.');
          isValid = false;
        } else {
          this.clearFieldError(fieldName);
        }
      }

      return isValid;
    }

    showFieldError(fieldName, message) {
      const errorContainer = this.formEl.querySelector(`[data-error-for="${fieldName}"]`);
      const fieldInput = this.formEl.querySelector(`[name="${fieldName}"]`);
      if (errorContainer) {
        errorContainer.textContent = message;
        errorContainer.style.display = 'block';
      }
      if (fieldInput) {
        fieldInput.classList.add('has-error');
        fieldInput.setAttribute('aria-invalid', 'true');
      }
      if (fieldName === 'phone') {
        const phoneGroup = this.formEl.querySelector('[data-phone-group]');
        if (phoneGroup) phoneGroup.classList.add('has-error');
      }
    }

    clearFieldError(fieldName) {
      const errorContainer = this.formEl.querySelector(`[data-error-for="${fieldName}"]`);
      const fieldInput = this.formEl.querySelector(`[name="${fieldName}"]`);
      if (errorContainer) {
        errorContainer.textContent = '';
        errorContainer.style.display = 'none';
      }
      if (fieldInput) {
        fieldInput.classList.remove('has-error');
        fieldInput.removeAttribute('aria-invalid');
      }
      if (fieldName === 'phone') {
        const phoneGroup = this.formEl.querySelector('[data-phone-group]');
        if (phoneGroup) phoneGroup.classList.remove('has-error');
      }
    }

    showAlert(message) {
      if (this.errorBanner) {
        this.errorBanner.textContent = message;
        this.errorBanner.removeAttribute('hidden');
      }
    }

    clearAlert() {
      if (this.errorBanner) {
        this.errorBanner.textContent = '';
        this.errorBanner.setAttribute('hidden', '');
      }
    }

    /**
     * Submit data to Google Sheets / Webhook
     */
    async handleFormSubmit() {
      if (this.isSubmitting) return;
      this.clearAlert();

      if (!this.validateForm()) return;

      const nameInput = this.formEl.querySelector('input[name="name"]');
      const phoneInput = this.formEl.querySelector('input[name="phone"]') ||
                         this.formEl.querySelector('input[name="contact"]');
      const questionInput = this.formEl.querySelector('textarea[name="question"]');

      const nameVal = nameInput ? nameInput.value.trim() : '';
      const rawPhone = phoneInput ? phoneInput.value.trim() : '';
      const phoneDigits = rawPhone.replace(/\D/g, '');
      const fullPhone = phoneDigits ? `+91 ${phoneDigits}` : '';
      const questionVal = (questionInput && questionInput.value.trim()) || 'The Urban Edit';

      // Set personalized Name in Success view
      const nameDisplays = this.popupEl.querySelectorAll('[data-support-popup-name-display]');
      nameDisplays.forEach((el) => {
        el.textContent = nameVal;
      });

      // Send the clean 10-digit number as contact/phone so Google Sheets/Excel
      // does not parse leading '+' as an invalid formula (#ERROR!)
      const payload = {
        name: nameVal,
        phone: phoneDigits,
        contact: phoneDigits,
        countryCode: '+91',
        phoneWithCode: `+91 ${phoneDigits}`,
        question: questionVal,
        source: 'The Urban Edit Popup',
        date: new Date().toISOString(),
        formattedDate: new Date().toLocaleString(),
        pageUrl: window.location.href,
        cartUrl: this.config.cartUrl,
        userAgent: navigator.userAgent
      };

      this.setSubmitting(true);

      try {
        if (!this.config.webhookUrl) {
          console.warn('The Urban Edit Popup: Webhook URL not set. Simulating successful submission.');
          await new Promise((resolve) => setTimeout(resolve, 700));
        } else {
          const response = await fetch(this.config.webhookUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'text/plain;charset=utf-8'
            },
            body: JSON.stringify(payload)
          });

          if (!response.ok && response.status !== 0 && response.type !== 'opaque') {
            throw new Error(`Server responded with status: ${response.status}`);
          }
        }

        Storage.set(STORAGE_KEYS.COMPLETED, 'true');
        this.showSuccessView();

        // Allow user 5 seconds to read the success message before auto-closing
        setTimeout(() => {
          this.close();
        }, 5000);

      } catch (error) {
        console.error('The Urban Edit Popup: Submission failed', error);
        this.showAlert('Unable to submit your request. Please check your connection and try again.');
      } finally {
        this.setSubmitting(false);
      }
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      if (this.submitBtn) {
        if (isSubmitting) {
          this.submitBtn.setAttribute('disabled', 'disabled');
          this.submitBtn.classList.add('is-loading');
        } else {
          this.submitBtn.removeAttribute('disabled');
          this.submitBtn.classList.remove('is-loading');
        }
      }
    }

    /**
     * Trap keyboard focus inside popup for accessibility
     */
    trapFocus() {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ];

      const handleKey = (e) => {
        if (!this.isOpen()) {
          document.removeEventListener('keydown', handleKey);
          return;
        }

        if (e.key !== 'Tab') return;

        const focusables = Array.from(this.dialogEl.querySelectorAll(focusableSelectors.join(',')))
          .filter((el) => el.offsetParent !== null);

        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };

      document.addEventListener('keydown', handleKey);
    }

    getStatus() {
      const domPresent = !!document.querySelector('.support-popup');
      const drawer = document.querySelector('cart-drawer');
      const isDrawerOpen = drawer && drawer.classList.contains('is-open');

      const info = {
        '1. Popup Enabled': this.config.enabled,
        '2. Cart Drawer Currently Open': isDrawerOpen ? 'YES' : 'NO',
        '3. 5-Second Dwell Timer Configured': `${this.config.openTimerSeconds}s`,
        '4. Trigger on Close Enabled': this.config.triggerOnClose ? 'YES' : 'NO',
        '5. Webhook URL Configured': this.config.webhookUrl ? 'YES' : 'NO (simulated)',
        '6. DOM Element Attached': domPresent
      };

      if (console && console.table) {
        console.table(info);
      }
      return info;
    }
  }

  let instance = null;

  function start() {
    if (!instance) {
      instance = new SupportPopupController();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Developer & Merchant Console API
  window.SupportPopup = {
    open: (reason = 'manual') => {
      if (instance) {
        instance.open(reason);
        console.log('✅ [Support Popup] Opened via SupportPopup.open()');
      }
    },
    close: () => {
      if (instance) instance.close();
    },
    simulateCheckoutReturn: () => {
      if (instance) {
        instance.closeCartDrawerQuietly();
        instance.open('checkout_return');
      }
      console.log('✅ [Support Popup] Checkout return simulated! Cart drawer closed and popup displayed.');
    },
    status: () => instance && instance.getStatus(),
    reset: () => {
      Storage.remove(STORAGE_KEYS.WENT_TO_CHECKOUT);
      Storage.remove(STORAGE_KEYS.CHECKOUT_TIMESTAMP);
      Storage.remove('support_popup_returned_from_checkout');
      Storage.remove(STORAGE_KEYS.COMPLETED);
      console.log('🔄 [Support Popup] Session storage cleared.');
    }
  };
})();
